import { useEffect, useRef, useState } from "react";
import { X, Loader2, Brain, Send } from "lucide-react";
import { getSupabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface Props {
  accountId: string;
  accountName?: string;
  onClose: () => void;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

async function streamSSE(
  response: Response,
  onDelta: (delta: string) => void,
  cancelledRef: { current: boolean },
) {
  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    throw new Error(json.error || `Error ${response.status}`);
  }
  if (!response.body) throw new Error("No response body");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done || cancelledRef.current) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (raw === "[DONE]") break;
        try {
          const parsed = JSON.parse(raw);
          const delta = parsed?.choices?.[0]?.delta?.content;
          if (delta) onDelta(delta);
        } catch { /* skip malformed chunks */ }
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;
  return headers;
}

export function CatchMeUpModal({ accountId, accountName, onClose }: Props) {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef({ current: false });
  const scrollRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [chatStreaming, setChatStreaming] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  useEffect(() => {
    const cancelledRefLocal = { current: false };
    cancelledRef.current = cancelledRefLocal;

    async function stream() {
      try {
        const headers = await authHeaders();
        const response = await fetch(`/api/accounts/${accountId}/ai-summary`, { headers });
        setLoading(false);
        await streamSSE(response, (delta) => setText(prev => prev + delta), cancelledRefLocal);
      } catch (err: any) {
        if (!cancelledRefLocal.current) setError(err.message || "Failed to load summary");
        setLoading(false);
      }
    }

    stream();
    return () => {
      cancelledRefLocal.current = true;
    };
  }, [accountId]);

  // Auto-scroll as text/messages stream in
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text, messages, chatStreaming]);

  async function sendMessage() {
    const content = input.trim();
    if (!content || chatStreaming) return;
    setChatError(null);
    setInput("");
    const nextMessages: ChatMessage[] = [...messages, { role: "user", content }];
    setMessages([...nextMessages, { role: "assistant", content: "" }]);
    setChatStreaming(true);

    const cancelledRefLocal = { current: false };
    try {
      const headers = await authHeaders();
      const response = await fetch(`/api/accounts/${accountId}/ai-chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({ messages: nextMessages }),
      });
      await streamSSE(response, (delta) => {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: updated[updated.length - 1].content + delta,
          };
          return updated;
        });
      }, cancelledRefLocal);
    } catch (err: any) {
      const msg = err.message || "Failed to send message";
      setChatError(msg);
      toast({ title: "Chat failed", description: msg, variant: "destructive" });
      // drop the empty assistant placeholder so retry starts clean
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setChatStreaming(false);
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const clientLabel = accountName || "this client";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-background rounded-xl shadow-2xl border border-border w-full max-w-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 shrink-0">
            <Brain className="w-4 h-4 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">Client AI · {clientLabel}</p>
            <p className="text-[11px] text-muted-foreground">AI briefing · Claude Sonnet</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Gathering context…</span>
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          {text && (
            <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {text}
              {loading && <span className="inline-block w-1.5 h-4 bg-violet-500 animate-pulse ml-0.5 align-middle" />}
            </div>
          )}

          {messages.length > 0 && (
            <div className="mt-4 pt-4 border-t space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-violet-600 text-white"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {m.content}
                    {chatStreaming && i === messages.length - 1 && m.role === "assistant" && (
                      <span className="inline-block w-1.5 h-4 bg-violet-500 animate-pulse ml-0.5 align-middle" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {chatError && (
            <p className="text-sm text-destructive mt-2">{chatError}</p>
          )}
        </div>

        {/* Chat input */}
        <div className="flex items-end gap-2 px-5 py-3 border-t shrink-0">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Ask anything about this client…"
            disabled={chatStreaming}
            rows={1}
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-60"
          />
          <button
            onClick={sendMessage}
            disabled={chatStreaming || !input.trim()}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {chatStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2 border-t shrink-0">
          <p className="text-[10px] text-muted-foreground">Based on open projects, notes, and recent activity</p>
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
